import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import QuoteDetailModal from './QuoteDetailModal';
import { Eye, Package, MessageSquare, Users, Clock, CheckCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface QuoteRequest {
  id: string;
  email: string;  // Full email for admin access
  phone: string | null;  // Full phone for admin access
  message: string | null;
  requested_quantity: number;
  status: string;
  admin_response: string | null;
  created_at: string;
  product_id: string | null;
  user_id: string | null;
  product_name: string;
  product_description: string;
}

const QuotesModule = () => {
  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState<QuoteRequest | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      // Use full access function for admin to see complete contact info
      const { data, error } = await supabase
        .rpc('get_admin_quote_requests_full_access');

      if (error) throw error;
      
      setQuotes(data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      // Enhanced error handling for security violations
      if (error.message?.includes('denied') || error.message?.includes('privileges')) {
        console.error('Access denied to quotes data');
        toast.error('אין הרשאות לצפייה בנתונים אלו');
      } else {
        toast.error('שגיאה בטעינת בקשות הצעת מחיר');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'responded':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'ממתין';
      case 'responded':
        return 'נענה';
      case 'rejected':
        return 'נדחה';
      default:
        return status;
    }
  };

  const getPendingQuotes = () => quotes.filter(q => q.status === 'pending').length;
  const getRespondedQuotes = () => quotes.filter(q => q.status === 'responded').length;
  const getTodayQuotes = () => {
    const today = new Date().toISOString().split('T')[0];
    return quotes.filter(q => q.created_at.startsWith(today)).length;
  };

  const handleViewQuote = (quote: QuoteRequest) => {
    setSelectedQuote(quote);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedQuote(null);
    setIsDetailModalOpen(false);
  };

  const handleQuoteUpdated = () => {
    fetchQuotes();
  };

  const handleDeleteQuote = async (quoteId: string) => {
    try {
      const { error } = await supabase.rpc('delete_quote_request_cascade', {
        request_id_param: quoteId
      });

      if (error) throw error;

      toast.success('בקשת הצעת מחיר נמחקה בהצלחה');
      fetchQuotes();
    } catch (error: any) {
      console.error('Error deleting quote request:', error);
      toast.error(`שגיאה במחיקת הבקשה: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען בקשות הצעת מחיר...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">סה"כ בקשות</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{quotes.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ממתינות למענה</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{getPendingQuotes()}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">נענו</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{getRespondedQuotes()}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">בקשות היום</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{getTodayQuotes()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Quotes Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            בקשות הצעת מחיר
          </CardTitle>
        </CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">אין בקשות עדיין</h3>
              <p className="text-muted-foreground">
                בקשות הצעת מחיר יופיעו כאן
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right whitespace-nowrap">אימייל</TableHead>
                      <TableHead className="text-right whitespace-nowrap">מוצר</TableHead>
                      <TableHead className="text-right whitespace-nowrap">כמות</TableHead>
                      <TableHead className="text-right whitespace-nowrap">הודעה</TableHead>
                      <TableHead className="text-right whitespace-nowrap">סטטוס</TableHead>
                      <TableHead className="text-right whitespace-nowrap">תאריך</TableHead>
                      <TableHead className="text-right whitespace-nowrap">פעולות</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                  {quotes.map((quote) => (
                    <TableRow key={quote.id}>
                       <TableCell>
                          <div>
                            <div className="font-medium">{quote.email}</div>
                            {quote.phone && (
                              <div className="text-sm text-muted-foreground">{quote.phone}</div>
                            )}
                          </div>
                        </TableCell>
                       <TableCell>
                         {quote.product_name || 'מוצר לא מוגדר'}
                       </TableCell>
                      <TableCell className="font-medium">
                        {quote.requested_quantity.toLocaleString()}
                      </TableCell>
                      <TableCell className="max-w-48">
                        <div className="truncate">
                          {quote.message || 'אין הודעה'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(quote.status)}>
                          {getStatusText(quote.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(quote.created_at).toLocaleDateString('he-IL')}
                      </TableCell>
                       <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleViewQuote(quote)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              צפייה
                            </Button>
                            
                            <AlertDialog>
                             <AlertDialogTrigger asChild>
                               <Button variant="destructive" size="sm">
                                 <Trash2 className="w-4 h-4 mr-2" />
                                 מחק
                               </Button>
                             </AlertDialogTrigger>
                             <AlertDialogContent dir="rtl">
                               <AlertDialogHeader>
                                 <AlertDialogTitle>האם אתה בטוח?</AlertDialogTitle>
                                 <AlertDialogDescription>
                                   פעולה זו תמחק את בקשת הצעת המחיר לצמיתות. לא ניתן לבטל פעולה זו.
                                 </AlertDialogDescription>
                               </AlertDialogHeader>
                               <AlertDialogFooter>
                                 <AlertDialogCancel>ביטול</AlertDialogCancel>
                                 <AlertDialogAction 
                                   onClick={() => handleDeleteQuote(quote.id)}
                                   className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                 >
                                   מחק
                                 </AlertDialogAction>
                               </AlertDialogFooter>
                             </AlertDialogContent>
                           </AlertDialog>
                         </div>
                       </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Quote Detail Modal */}
      <QuoteDetailModal
        quote={selectedQuote}
        isOpen={isDetailModalOpen}
        onClose={handleCloseModal}
        onQuoteUpdated={handleQuoteUpdated}
      />
    </div>
  );
};

export default QuotesModule;